import { ChMetricSchema, CRUDInstallationBareSchema, CRUDInstallationSchema, InputService, StateService, V1InstallationWithState } from "@/client"
import { getApiConfig } from "@/config"
import { Metric, MetricCollection } from "@/contexts/state"
import { useToken } from "@/contexts/user"
import { ChMetricSchemaDb, db } from "@/db"
import { useLogProvider } from "@/providers/log"
import { sortObjectByKey } from "@/utility/sort"
import { QueryObserverBaseResult, useMutation, UseMutationOptions, UseMutationResult, useQuery, UseQueryOptions } from "@tanstack/react-query"
import dayjs from "dayjs"
import 'dayjs/locale/nb'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import _ from 'lodash'
import { signOut } from "next-auth/react"
import { useSearchParams } from "next/navigation"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { useSocket } from "../ws"
import { fetchData, FetchDataOptions, FetchError, ServiceFunction } from "./fetch"
import { FetchInterface, PrefetchOptions } from "./server"

//dayjs.locale('nb')
dayjs.extend(localizedFormat)
dayjs.extend(relativeTime)
dayjs.extend(utc)

type CommandListener = (payload: any) => void;

class CommandBus {
  private listeners = new Map<string, Set<CommandListener>>();

  on(commandId: string, listener: CommandListener) {
    console.log('Registering listener for commandId', commandId);
    const set = this.listeners.get(commandId) ?? new Set();
    set.add(listener);
    this.listeners.set(commandId, set);
    return () => {
      console.log('Unregistering listener for commandId', commandId);
      set.delete(listener);
    }
  }

  emit(commandId: string, payload: any) {
    const set = this.listeners.get(commandId);
    if (!set) return;
    set.forEach(listener => listener(payload));
  }
}

const commandBus = new CommandBus();

type ResultFn = (result: boolean, data?: any) => void;

export const useCommand = ({
  id,
  onUpdate,
}: {
  id: string,
  onUpdate?: CommandListener
}): ({
  waitForId: (id: number, resultFn: ResultFn, timeout?: number) => void,
  sendCommand: (params: { command: string, params?: Record<string, any>, onComplete?: (success: boolean, data?: any) => void }) => Promise<void>,
}) => {
  const callbackRef = useRef(onUpdate);
  const waitForRef = useRef<{ id: number, resultFn: ResultFn, timeout: number, result: boolean | null } | null>(null)

  const { mutate: sendCommand } = useApiMutation(InputService.inputSendCommand)

  const cmdWaitfor = useCallback((data: any) => {
    if (!waitForRef.current) {
      return
    }

    if (waitForRef.current.id === data.msg.id) {
      console.log('WAIT FOR RESULT', data.msg.response)
      waitForRef.current.resultFn(data.msg.status as boolean, data.msg || null)
      waitForRef.current = null
    }
  }, [])

  // keep ref up-to-date without re-running useEffect
  useEffect(() => {
    callbackRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    const unsubscribe = commandBus.on(id, (data) => {
      callbackRef.current?.(data)
      cmdWaitfor(data)
    });
    return () => { unsubscribe(); };
  }, [id, cmdWaitfor]);

  const waitForId = useCallback((id: number, resultFn: ResultFn, timeout: number = 10000) => {
    waitForRef.current = { id, resultFn, timeout, result: null }
    const timeoutId = setTimeout(() => {
      if (waitForRef.current?.id === id && waitForRef.current.result === null) {
        waitForRef.current.resultFn(false)
        waitForRef.current = null
      }
    }, timeout);

    return () => { clearTimeout(timeoutId); };
  }, []);

  const sendCommandWrapped = useCallback(async ({
    command,
    params,
    onComplete,
  }: {
    command: string,
    params?: Record<string, any>,
    onComplete?: (success: boolean, data?: any) => void,
  }) => {
    const cmdId = Date.now()

    waitForId(cmdId, (result, data) => {
      onComplete?.(result, data)
    })

    await sendCommand({
      body: {
        command_id: cmdId,
        transporter: 'aws_mqtt_esp_generic',
        device_id: id,
        command,
        params: params || {}
      }
    }, {
      onSuccess: () => {
        console.log('Command sent')
      },
      onError: () => {
        console.log('Failed to send command')
        onComplete?.(false)
      }
    })
  }, [id, sendCommand, waitForId]);

  return {
    waitForId,
    sendCommand: sendCommandWrapped,
  }
};

export const useApiMutation = <RequestData extends { query?: any, path?: any, body?: any }, ResponseData, TError = unknown, ThrowOnError extends boolean = false>(
  func: ServiceFunction<RequestData, ResponseData, TError, ThrowOnError> | undefined,
  useMutationOptions?: { useMutationOptions?: Omit<UseMutationOptions<ResponseData>, 'queryKey'> },
): UseMutationResult<ResponseData | undefined, unknown, RequestData> => {
  //): UseMutationResult<ResponseData | undefined, unknown, { body?: any; query?: any; path?: any; }> => {
  const token = useToken()

  return useMutation({
    mutationFn: async (options) => {
      if (!func) {
        throw Error('Missing function')
      }

      try {
        const { data: result } = await fetchData({
          func,
          ...options
        } as FetchDataOptions<RequestData, ResponseData, TError, ThrowOnError>, token)// as unknown as FetchDataOptions<RequestData, ResponseData, TError, ThrowOnError>, token)

        return result

      } catch (error) {
        console.log('error', error)
        throw error
      }
    },
    //throwOnError: true,
    onSuccess: () => {
      console.log('success')
    },
    onError: (error) => {
      console.log('error on mutation', error)
      toast.error('Error on mutation: ' + (error as any)?.message)
    }
  })
}

export const useApiQuery = <RequestData, ResponseData, TError = unknown, ThrowOnError extends boolean = false>({
  queryKey,
  options,
  staticKey = false,
  useQueryOptions
}: PrefetchOptions<RequestData, ResponseData, TError, ThrowOnError> & {
  useQueryOptions?: Omit<UseQueryOptions<ResponseData>, 'queryKey' | 'queryFn'>
}): QueryObserverBaseResult<ResponseData | undefined, unknown> => {
  const token = useToken() || options.token

  const qk = staticKey ? queryKey : queryKey.concat(Object.values(sortObjectByKey(options.query || {})))

  const result = useQuery({
    ...useQueryOptions,
    queryKey: qk,
    queryFn: async (context) => {
      //console.log('qc', qk, qc.getQueryCache().findAll({ queryKey: qk }))
      try {
        const { data } = await fetchData(options, token)
        return data!

      } catch (error) {
        if (error instanceof FetchError && error?.response?.status === 401) {
          console.error('Unauthorized', error.response)
          signOut({ callbackUrl: "/" });
        }

        throw error
      }
    },
    retry: (failureCount, error) => {
      if (error instanceof FetchError && error?.response?.status === 404) {
        console.log('not found', failureCount, error)
        return false
      }

      return true
    },
    throwOnError: (error) => {
      if (error instanceof FetchError && error?.response?.status === 404) {
        return false
      }

      console.log('error', error, typeof error)
      return true
    }
  })

  return result
}


export const useApiQueryConfig = <RequestData, ResponseData, TError = unknown, ThrowOnError extends boolean = false>({
  func,
  overrides,
  queryKey,
  useQueryOptions,
  staticKey = false,
}: FetchInterface<RequestData, ResponseData, TError, ThrowOnError> & {
  useQueryOptions?: Omit<UseQueryOptions<ResponseData>, 'queryKey' | 'queryFn'>,
}): QueryObserverBaseResult<ResponseData | undefined, unknown> => {
  const sp = useSearchParams()
  const config = getApiConfig(
    func,
    _.merge({
      query: Object.fromEntries(Array.from(sp.entries()).filter(([key, val]) => !['token'].includes(key))),
    }, overrides),
    queryKey
  )

  return useApiQuery({
    ...config,
    queryKey: queryKey || config.queryKey,
    staticKey,
    useQueryOptions
  })
}

export const useCountQuery = <RequestData, ResponseData, TError = unknown, ThrowOnError extends boolean = false>(
  opts: Omit<FetchInterface<RequestData, ResponseData, TError, ThrowOnError> & {
    useQueryOptions?: Omit<UseQueryOptions<ResponseData>, 'queryKey' | 'queryFn'>,
    service: any,
    name: string,
  }, 'func' | 'queryKey' | 'staticKey'>
) => {
  return useApiQueryConfig({
    func: opts.service?.count,
    queryKey: ['query', opts.name, 'count'],
    staticKey: true,
    ...opts,
  })
}

export class MetricManager {
  loaded = false
  private buffer: ChMetricSchema[] = []
  private flushTimer: any = null

  updateFromSocket(m: ChMetricSchema) {
    this.buffer.push(m)
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flush(), 2000)
    }
  }

  private keyOf(d: ChMetricSchemaDb) {
    return [d.id, d.device_type, d.device_number, d.signal] as const;
  }
  private keyString(d: ChMetricSchemaDb) {
    return `${d.id}|${d.device_type}|${d.device_number}|${d.signal}`;
  }

  private async flush() {
    //console.log('Flushing', this.buffer.map(m => m.signal))
    const batch = this.buffer; this.buffer = []; this.flushTimer = null;
    if (!batch.length) return;

    const latestByKey = new Map<string, ChMetricSchemaDb>();
    for (const m of batch) latestByKey.set(this.keyString(m), m);

    const keys = Array.from(latestByKey.values()).map(m => this.keyOf(m));

    await db.transaction('rw', db.state, async () => {
      //console.log(`Flushing ${keys.length} metrics`);

      await db.state
        .where('[id+device_type+device_number+signal]')
        .anyOf(keys as any)
        .modify(obj => {
          const k = `${obj.id}|${obj.device_type}|${obj.device_number}|${obj.signal}`;
          const m = latestByKey.get(k);
          if (!m) return;

          obj.vi = m.vi;
          obj.vf = m.vf;
          obj.vs = m.vs;
          obj.vo = m.vo;
          obj.vb = m.vb;

          obj.ts = m.ts;
          obj.timestamp = dayjs.utc(m.ts).unix();
        });


      const existing = await db.state.bulkGet(keys);
      const toAdd: ChMetricSchemaDb[] = [];
      existing.forEach((row, i) => {
        if (!row) {
          const m = latestByKey.get(keys[i].join('|'))!;
          toAdd.push({
            ...m,
            timestamp: dayjs.utc(m.ts).unix(),
            identifier: m.identifier || '',
          });
        }
      });
      if (toAdd.length) await db.state.bulkAdd(toAdd);
    });
  }

  /*
  update(data: ChMetricSchema, timeFilter: boolean = true) {
    //console.log('update', data)
    return db.transaction('rw', db.state, async () => {
      const currentValue = await db.state.get([data.id, data.device_type, data.device_number, data.signal])

      if (currentValue) {
        const timeDiff = dayjs.utc(data?.ts).diff(dayjs.utc(currentValue?.ts), 'millisecond')
        //console.log('currentValue', currentValue, timeDiff, timeDiff/1000)

        // Add time buffer on rapid updates
        if (timeFilter && timeDiff < (3 * 1000)) {
          return
        }

        const result = await db.state.update(
          [data.id, data.device_type, data.device_number, data.signal],
          {
            ...data,
            timestamp: dayjs.utc(data.ts).unix(),
            identifier: data.identifier || '',

          }
        )

        if (result === 0) {
          console.warn('Unable to update metric')
        }
      } else {
        try {
          await db.state.add({
            ...data,
            timestamp: dayjs.utc(data.ts).unix(),
            identifier: data.identifier || '',
            key: null,
          });
        } catch (error) {
          console.log('error', error, data)
        }
      }
    });
  }
  */

  updateBulk(data: ChMetricSchema[], prune: string[] = [], debug: boolean = false) {
    this.loaded = true

    return db.transaction('rw', db.state, async () => {
      if (prune) {
        await this.prune(prune, debug)
      }

      const result = await db.state.bulkPut(
        data.map(d => ({
          ...d,
          timestamp: dayjs.utc(d.ts).unix(),
          identifier: d.identifier || '',
        })),
        { allKeys: true }
      )

      if (debug) {
        console.log(`Updated ${result.length}/${data.length} metrics in bulk`, result, prune)
      }
    });
  }

  async prune(ids: string[], debug: boolean = false) {
    if (ids.length === 0) {
      return
    }

    const cutoff = dayjs().subtract(1, 'day').unix()

    const result = await db.state
      .where('id')
      .anyOf(ids)
      .filter(m => m.timestamp !== undefined && m.timestamp < cutoff)
      .delete()

    if (result > 0 && debug) {
      console.log(`Pruned ${result} old metrics`)
    }
  }
}

const INSTALLATION_IOT_CHANNELS_PREFIX = ['iot:id.', 'iot:event.', 'iot:cmd.']

export const useInstallationState = ({
  installation,
  token,
  gateways = null,
  currentState = null,
  fetchToFront = false,
  fetchFromServer = true,
}: {
  installation?: Pick<CRUDInstallationSchema | V1InstallationWithState, 'id' | 'name' | 'gateways' | 'iot_states'> | Pick<CRUDInstallationBareSchema, 'id' | 'name' | 'gateways'>,
  token?: string,
  gateways?: string[] | null,
  currentState?: MetricCollection | null,
  fetchToFront?: boolean,
  fetchFromServer?: boolean,
}): {
  state: MetricCollection,
  manager: MetricManager,
  gateways: string[],
  updateComplete: boolean,
  addCallback: (cb: (cbType: string, channel: string, data: any) => void) => () => void,
  removeCallback: (cb: (cbType: string, channel: string, data: any) => void) => void,
} => {
  const [manager, setManager] = useState<MetricManager>(new MetricManager())
  const [state, setState] = useState<MetricCollection>(currentState ?? MetricCollection.fromArray((installation as any)?.iot_states ?? []))
  const [updateComplete, setUpdateComplete] = useState(false)
  const [updateTime, setUpdateTime] = useState<number>(new Date().getTime())

  const [cbList, setCbList] = useState<((cbType: string, channel: string, data: any) => void)[]>([])

  const gwlist = useMemo(() => gateways ?? installation?.gateways?.map(g => g.id) ?? [], [installation, gateways])
  const gwids = useMemo(() => gwlist.map(
    i => INSTALLATION_IOT_CHANNELS_PREFIX.map(prefix => prefix + i)
  ).flat() ?? [], [gwlist])

  const { data: stateData, dataUpdatedAt, isFetched } = useApiQueryConfig({
    func: StateService.stateGetStateById,
    overrides: {
      query: {
        id: installation?.id!,
        only_fetch_to_front: fetchToFront,
      },
      token: token,
    },
    queryKey: ['state', installation?.id, fetchToFront],
    staticKey: true,
    useQueryOptions: {
      enabled: !!installation && fetchFromServer,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      staleTime: 1000 * 60 * 1, // 1 minute
      initialData: () => {
        return !fetchToFront ? undefined : (installation as any)?.iot_states ?? undefined
      },
    }
  })

  const { addItem } = useLogProvider()

  const addCallback = useCallback((cb: (cbType: string, channel: string, data: any) => void) => {
    setCbList((prev) => [...prev, cb])
    return () => {
      setCbList((prev) => prev.filter(c => c !== cb))
    }
  }, [])

  const removeCallback = useCallback((cb: (cbType: string, channel: string, data: any) => void) => {
    setCbList((prev) => prev.filter(c => c !== cb))
  }, [])

  const socketCallback = useCallback((data: any) => {
    const cbType = data.channel?.split('.')[0] || ''
    cbList?.map(cb => cb(cbType, data.channel, data.data))


    if (data.channel?.startsWith('iot:id.')) {
      manager.updateFromSocket(data.data)
      state.updateMetric(data.data)
    } else if (data.channel?.startsWith('iot:event.')) {
      console.log('GOT EVENT', data)
      addItem({
        ts: Date.now(),
        level: 'info',
        type: 'event',
        message: data.data.event_name,
        details: data.data,
      })

      if (data.data.event_type === 'CHANGE') {
        toast.success('Got event from ' + data.data.device.signal)
      } else if (data.data.event_type === 'DMS') {
        toast.error('Lost contact with ' + data.data.device.signal)
      } else if (data.data.event_type === 'SYSTEM') {
        if (data.data.event_name === 'mqtt.connected') {
          toast(
            data.data.state?.installation?.name + ' (' + data.data.state?.customer?.name + ') connected to MQTT broker',
            {
              position: 'bottom-right',
            }
          )
        } else if (data.data.event_name === 'mqtt.disconnected') {
          toast(
            data.data.state?.installation?.name + ' (' + data.data.state?.customer?.name + ') disconnected from MQTT broker',
            {
              position: 'bottom-right',
            }
          )
        }
      } else if (data.data.event_name === 'signal.absent.online') {
        toast.error(
          'Device went offline: ' + data.data.state?.installation?.name + ' (' + data.data.state?.customer?.name + ')',
          {
            position: 'bottom-right',
          }
        )
      } else if (data.data.event_name === 'signal.present.online') {
        toast.success(
          'Device came online: ' + data.data.state?.installation?.name + ' (' + data.data.state?.customer?.name + ')',
          {
            position: 'bottom-right',
          }
        )
      } else if (data.data.event_name === 'signal.absent.boring') {
        toast(
          'Device stopped boring: ' + data.data.state?.installation?.name + ' (' + data.data.state?.customer?.name + ')',
          {
            position: 'bottom-right',
          }
        )
      } else if (data.data.event_name === 'signal.present.boring') {
        toast(
          'Device started boring: ' + data.data.state?.installation?.name + ' (' + data.data.state?.customer?.name + ')',
          {
            position: 'bottom-right',
          }
        )
      }
    } else if (data.channel?.startsWith('iot:cmd.')) {
      console.log('got command response', data)
      // TODO: Implement callback handling
      const { id, status } = data.data ?? {};
      if (id) commandBus.emit(id, data.data);

      addItem({
        ts: Date.now(),
        level: 'info',
        type: 'command',
        message: `Response for "${data.data.msg.command}": ${data.data.msg.status ? 'SUCCESS' : 'FAILURE'}`,
        details: data.data,
      })
    }
  }, [manager, state, addItem, cbList])


  useSocket({
    id: 'installation',
    channels: gwids,
    callback: socketCallback,
  })

  useEffect(() => {
    if (!isFetched) {
      return
    }

    //console.log('state data updated', installation?.name, stateData?.length)

    const update = async () => {
      manager.updateBulk(
        stateData?.filter((m: any) => m.device_type !== null && m.device_number !== null) ?? [],
        gwlist,
      )
        .then(() => {
          //console.log('database updated')
          //manager.prune(gwlist)

          setUpdateComplete(true)
          setState(MetricCollection.fromArray(stateData ?? []))
          //setState(MetricCollection.fromArray((state?.metricValues ?? []).concat(stateData?.filter((m: any) => m.device_type !== null && m.device_number !== null) ?? [])))
        })
        .catch(err => {
          console.log('database update error', err)
        })
    }

    update()
  }, [stateData, manager, gwlist, isFetched])

  return { state, manager, gateways: gwlist, updateComplete, addCallback, removeCallback }
}



export const useLocalState = ({
  currentState = null,
  gateways,
  identifier,
}: {
  currentState?: MetricCollection | null,
  gateways: string[],
  identifier?: string,
}): {
  metrics: Metric[],
} => {
  const [state, setState] = useState<Record<string, Metric>>(
    currentState?.metricValues
      .filter(m => identifier === undefined || m.identifier === identifier)
      .reduce((acc, m) => ({ ...acc, [m.key]: m }), {}) ?? {}
  )

  const gwids = useMemo(() => gateways.map(
    i => INSTALLATION_IOT_CHANNELS_PREFIX.map(prefix => prefix + i)
  ).flat() ?? [], [gateways])

  const socketCallback = useCallback((data: any) => {
    if (data.channel?.startsWith('iot:id.')) {
      if (identifier !== undefined && data.data.identifier !== identifier) {
        return
      }

      const metric = new Metric(data.data)
      const current = state[metric.key]//state.identifier(data.data.identifier).signal(data.data.signal)
      //console.log('LOCAL GOT METRIC', metric.key, metric, current, current?.getValue(), data.data.vf)
      if (current?.getValue() !== data.data.vf) {
        //console.log('LOCAL GOT METRIC', data.data.identifier, data.data.signal, data.data.vf, current?.getValue())
        setState((prev) => ({ ...prev, [metric.key]: metric }))
      }
    }
  }, [state, identifier])


  useSocket({
    id: 'installation',
    channels: gwids,
    callback: socketCallback,
  })

  const metrics = useMemo(() => Object.values(state), [state])

  return { metrics }
}